import { Component, inject, computed } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd, Data } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { FooterComponent } from './shared/components/footer/footer.component';
import { NavbarComponent } from './shared/components/navbar/navbar.component';
import { TuiRoot, TuiDialogService } from '@taiga-ui/core';
import { TuiResponsiveDialogService } from '@taiga-ui/addon-mobile';
import { AuthService } from './features/auth/services/auth.service';
import { ConfirmService } from './shared/services/confirm.service';
import { HelpButtonComponent } from './features/support/components/help-button/help-button.component';
import { SessionExpiryNotifierService } from './core/services/session-expiry-notifier.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NavbarComponent, FooterComponent, TuiRoot, HelpButtonComponent],
  // Dialogs open as bottom sheets on small screens. The alias must live on a
  // COMPONENT injector, not in app.config: TuiResponsiveDialogService itself
  // injects TuiDialogService, so aliasing the token in the root environment
  // injector is a circular dependency (NG0200). Here the responsive service
  // still resolves the base service from the environment injector, while
  // every component below <app-root> gets the responsive one. ConfirmService
  // is re-provided so its confirms resolve the alias too.
  providers: [
    { provide: TuiDialogService, useExisting: TuiResponsiveDialogService },
    ConfirmService,
  ],
  templateUrl: './app.html',
  styleUrl: './app.less'
})
export class App {
  private authService = inject(AuthService);
  // Forces singleton instantiation so its sessionExpired$ subscription starts at app boot.
  private sessionExpiryNotifier = inject(SessionExpiryNotifierService);
  private router = inject(Router);

  // Data of the deepest activated route; routes opt into the bare layout with
  // `data: { fullscreen, hideNavbar }` (the HD OBS views).
  private routeData = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.leafRouteData())
    ),
    { initialValue: this.leafRouteData() }
  );

  isFullscreen = computed(() => this.routeData()['fullscreen'] === true);

  showNavbar = computed(() => this.routeData()['hideNavbar'] !== true);

  isLoggedIn = computed(() => !!this.authService.currentUser());

  constructor() {
    this.authService.initialize();
  }

  // Route data rather than URL matching, so query params (`/hd?lang=ru`) don't matter.
  private leafRouteData(): Data {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) route = route.firstChild;
    return route.data;
  }
}
